// ============================================================
// موتور دستور — از چت‌بات تا Agent
//
// detectIntent یک تابع خالص است: رشته می‌گیرد، یک شیء دستور یا null
// برمی‌گرداند. هیچ وابستگی شبکه‌ای ندارد، پس آنی است و رایگان.
//
// قاعده‌ی بنیادی: «دستور، فعل می‌خواهد».
//   «اطلاعات خبازی چیه؟»        → سوال (null)
//   «منو ببر به اطلاعات خبازی»  → دستور ناوبری
//
// و یک قاعده‌ی مخصوص این پروژه: فعل‌های «بده» و «کن» از فهرست
// فعل‌های رفتن بیرون‌اند. «اطلاعات خبازی رو بده» یعنی داده را در
// همین پنجره نشانم بده، نه اینکه صفحه عوض شود.
// ============================================================

import { SitePage, canAccess, SITE_PAGES } from "./access";
import { hasAny, longestMatch, normalize } from "./text";

export type Intent =
    | { type: "greeting"; reply: string }
    | { type: "navigate"; link: string; title: string; reply: string }
    | { type: "export"; pageId?: string; title?: string; reply: string }
    | { type: "filter"; pageId?: string; title?: string; sentence: string; reply: string }
    | { type: "clear-filter"; pageId?: string; title?: string; reply: string }
    | { type: "describe"; reply: string }
    | { type: "menu"; reply: string }
    | { type: "help"; reply: string }
    | { type: "whoami"; reply: string }
    | { type: "close"; reply: string }
    | { type: "clear"; reply: string }
    | { type: "cancel"; reply: string }
    | { type: "voice-on"; reply: string }
    | { type: "voice-off"; reply: string }
    | { type: "speak-on"; reply: string }
    | { type: "speak-off"; reply: string };

/**
 * فعل‌های بی‌ابهامِ «رفتن».
 *
 * عمداً «کن» و «بده» اینجا نیستند: این دو در فارسی تقریباً همه‌جا
 * می‌آیند («توضیح بده» یک سوال است) و اگر باشند، کاربر به جای جواب
 * به صفحه‌ی دیگری پرت می‌شود.
 *
 * «نشونم بده» و «بیار» هم بیرون‌اند — و این تفاوت مهمِ یک پنل داده
 * با یک سایت شخصی است. «اطلاعات خبازی رو نشونم بده» یعنی رکوردها را
 * همین‌جا نشانم بده، نه اینکه صفحه عوض شود.
 */
const GO_VERBS = [
    "ببر", "برو", "بریم", "برم", "باز کن", "بازکن", "بازش کن",
    "بازکردن", "برو به", "میخوام برم", "می خوام برم", "منتقل", "صفحه",
];

const isCommand = (n: string) => hasAny(n, GO_VERBS);

/** واژه‌های «خروجی اکسل». */
export const EXCEL_WORDS = [
    "خروجی اکسل", "خروجی excel", "فایل اکسل", "دانلود اکسل", "اکسل بگیر",
    "اکسلش", "به اکسل", "excel", "اکسل", "xlsx",
];

/** واژه‌هایی که یعنی «روی صفحه فیلتر بگذار». */
export const FILTER_WORDS = [
    "فیلتر کن", "فیلترش کن", "فیلتر بزن", "فیلتر", "سرچ کن", "سرچش کن", "سرچ",
    "جستجو کن", "جستجو", "جست و جو", "پیدا کن", "پیداش کن", "بگرد",
];

/**
 * فعل‌های «نشانم بده».
 *
 * این‌ها در detectIntent دستور نیستند — «اطلاعات خبازی رو نشونم بده»
 * باید رکورد را در گفتگو بیاورد، نه صفحه را عوض کند. ولی موتور از
 * همین‌ها می‌فهمد که *علاوه بر* نمایش در گفتگو، همان فیلتر را روی
 * صفحه‌ی متناظر هم بگذارد.
 */
export const SHOW_VERBS = [
    "نمایش بده", "نمایش بدید", "نمایشش بده", "نشون بده", "نشونم بده",
    "نشونم بدید", "نشان بده", "نشانم بده", "نشون بدید", "لیست کن", "بیار",
    "بهم بده", "برام بیار",
];

/** بلندترین تگ صفحه‌ای که در جمله آمده است. */
const findPage = (n: string): SitePage | undefined => {
    let target: SitePage | undefined;
    let winnerLength = 0;

    for (let i = 0; i < SITE_PAGES.length; i++) {
        const page = SITE_PAGES[i];
        const match = longestMatch(n, page.tags.concat([page.title]));
        if (match && match.length > winnerLength) {
            target = page;
            winnerLength = match.length;
        }
    }

    return target;
};

export interface IntentContext {
    role: number | null;
}

/**
 * ترتیب بررسی اتفاقی نیست — مشخص‌تر باید زودتر بیاید.
 * «دستیار صوتی رو خاموش کن» قبل از «دستیار رو ببند» چک می‌شود،
 * و خاموش کردن قبل از روشن کردن، چون «فعال» زیررشته‌ی «غیرفعال» است.
 */
export const detectIntent = (message: string, ctx: IntentContext): Intent | null => {
    const n = normalize(message);
    if (!n) return null;

    // احوال‌پرسی کوتاه را مثل یک گفتگوی عادی جواب بده؛ این الگوها
    // عمداً محدودند تا سلامِ ابتدای یک دستور، خود دستور را نبلعد.
    if (
        ["سلام", "سلام خوبی", "سلام چطوری", "سلام حالت چطوره", "سلام شما خوبی", "درود", "خوبی", "چطوری", "صبح بخیر", "وقت بخیر"]
            .indexOf(n) !== -1
    ) {
        return {
            type: "greeting",
            reply: "سلام، ممنون خوبم! شما چطورید؟ بفرمایید، چه کمکی از دستم برمیاد؟",
        };
    }

    // ---------- ۱) خاموش کردن صدا (قبل از روشن کردن) ----------
    if (
        hasAny(n, [
            "صدا رو خاموش", "صدا خاموش", "خاموش کردن صدا", "نخون", "حرف نزن",
            "ساکت", "بی صدا", "پاسخ صوتی خاموش", "پاسخ صوتی رو خاموش", "صوت خاموش",
        ])
    ) {
        return { type: "speak-off", reply: "باشه، دیگر بلند نمی‌خوانم." };
    }

    // ---------- ۲) روشن کردن صدا ----------
    if (
        hasAny(n, [
            "صدا رو روشن", "صدا روشن", "بلند بخون", "برام بخون", "با صدا بگو",
            "پاسخ صوتی", "صوتی جواب بده", "حرف بزن",
        ])
    ) {
        return { type: "speak-on", reply: "باشه، از این به بعد جواب‌ها را بلند می‌خوانم." };
    }

    // ---------- ۳) خاموش کردن میکروفون ----------
    if (hasAny(n, ["میکروفون خاموش", "میکروفون رو خاموش", "گوش نده", "دیگه نشنو", "ضبط بسه", "شنیدن بسه"])) {
        return { type: "voice-off", reply: "میکروفون خاموش شد." };
    }

    // ---------- ۴) روشن کردن میکروفون ----------
    if (hasAny(n, ["میکروفون روشن", "میکروفون رو روشن", "گوش کن", "صداتو بشنو", "ضبط کن", "بشنو"])) {
        return { type: "voice-on", reply: "می‌شنوم. بفرمایید." };
    }

    // ---------- ۵) لغو گفتگوی نیمه‌کاره ----------
    if (hasAny(n, ["لغو", "بیخیال", "منصرف شدم", "کنسل", "فراموشش کن", "ولش کن", "نمیخوام"])) {
        return { type: "cancel", reply: "باشه، لغو شد. چیز دیگری بپرسید." };
    }

    // ---------- ۶) پاک کردن گفتگو ----------
    if (hasAny(n, ["گفتگو رو پاک", "پاک کردن گفتگو", "تاریخچه رو پاک", "از اول شروع", "ریست کن"])) {
        return { type: "clear", reply: "گفتگو پاک شد." };
    }

    // ---------- ۷) بستن پنل (بعد از دستورهای صوتی، چون مشخص‌تر بودند) ----------
    if (hasAny(n, ["دستیار رو ببند", "ببند دستیار", "پنل رو ببند", "بسته شو", "ببند"])) {
        return { type: "close", reply: "بستم. دکمه‌ی گوشه‌ی صفحه همیشه هست." };
    }

    // ---------- ۸) من کی هستم / نقشم چیست ----------
    if (hasAny(n, ["من کیم", "من کی هستم", "نقش من", "سطح دسترسی من", "دسترسی من چیه", "چه نقشی دارم"])) {
        return { type: "whoami", reply: "" };
    }

    // ---------- ۹) خروجی اکسل ----------
    //
    // قبل از ناوبری می‌آید، چون «خروجی اکسل *صفحه* اطلاعات خبازی» هم
    // واژه‌ی «صفحه» دارد هم نام صفحه — و با ترتیب برعکس، به جای گرفتن
    // فایل، صفحه عوض می‌شد.
    if (hasAny(n, EXCEL_WORDS)) {
        const page = findPage(n);
        if (!page) {
            return {
                type: "export",
                reply: "خروجی اکسل کدام بخش را می‌خواهید؟",
            };
        }
        if (!canAccess(ctx.role, page.roles)) {
            return {
                type: "export",
                reply: "دسترسی شما به «" + page.title + "» باز نیست، پس خروجی هم نمی‌گیرم.",
            };
        }
        return {
            type: "export",
            pageId: page.id,
            title: page.title,
            reply: "دارم خروجی اکسل «" + page.title + "» را می‌سازم…",
        };
    }

    // ---------- ۱۰) «اطلاعاتش را بگو» ----------
    //
    // بعد از هر نتیجه‌ای، روی هر صفحه‌ای: «فیلتر کن فرشته رضای درده»
    // و بعد «اطلاعاتشو بگو» → همان ردیف‌ها با صدا خوانده می‌شوند.
    //
    // شرط «نام صفحه در جمله نباشد» حیاتی است: «اطلاعات خبازی رضا» هم
    // واژه‌ی «اطلاعات» دارد، ولی آن یک پرسش تازه است نه درخواست
    // خواندن نتیجه‌ی قبلی.
    if (
        !findPage(n) &&
        hasAny(n, [
            "اطلاعاتشو بگو", "اطلاعاتش رو بگو", "اطلاعات شو بگو", "اطلاعاتش را بگو",
            "اطلاعاتشو بهم بده", "اطلاعات شو بهم بده", "اطلاعاتش رو بهم بده",
            "اطلاعاتشو بده", "اطلاعاتش رو بده", "اطلاعاتش را بده",
            "اطلاعاتشو نشون بده", "اطلاعات شو نشون بده", "اطلاعاتش رو نشون بده",
            "اطلاعاتشو بخون", "اطلاعاتش رو بخون", "اطلاعاتشو",
            "جزییاتش رو بگو", "جزئیاتش رو بگو", "جزییاتشو بگو", "جزئیاتشو بگو",
            "توضیح بده", "شرح بده", "شرحش بده", "بخونش", "بخون برام", "برام بخون",
            "همینو بگو", "همین رو بگو", "نتیجه رو بگو", "نتایج رو بگو",
            "بگو ببینم", "چی شد", "چی پیدا کردی",
        ])
    ) {
        return { type: "describe", reply: "" };
    }

    // ---------- ۱۱) برداشتن فیلتر ----------
    //
    // قبل از فیلتر می‌آید، وگرنه «فیلتر را پاک کن» به خاطر واژه‌ی
    // «فیلتر» دستور فیلتر شناخته می‌شد و «پاک» را — که فقط باقی‌مانده‌ی
    // جمله بود — داخل کادر جست‌وجو می‌گذاشت.
    if (
        hasAny(n, [
            "فیلتر رو پاک", "فیلتر را پاک", "پاک کردن فیلتر", "فیلتر پاک",
            "فیلتر رو بردار", "فیلتر را بردار", "برداشتن فیلتر",
            "فیلتر رو حذف", "فیلتر را حذف", "حذف فیلتر", "لغو فیلتر",
            "بدون فیلتر", "فیلتر نمیخوام", "فیلتر رو خالی",
        ])
    ) {
        const page = findPage(n);
        return {
            type: "clear-filter",
            pageId: page ? page.id : undefined,
            title: page ? page.title : undefined,
            reply: "",
        };
    }

    // ---------- ۱۱) فیلتر روی صفحه ----------
    //
    // «مهناز کافر فیلتر کن» → نام صفحه نیامده، یعنی همین صفحه‌ای که
    // کاربر رویش است. تصمیمِ «کدام صفحه» در موتور گرفته می‌شود، چون
    // آنجا مسیر جاری در دسترس است.
    if (hasAny(n, FILTER_WORDS)) {
        const page = findPage(n);
        if (page && !canAccess(ctx.role, page.roles)) {
            return {
                type: "filter",
                sentence: message,
                reply: "دسترسی شما به «" + page.title + "» باز نیست.",
            };
        }
        return {
            type: "filter",
            pageId: page ? page.id : undefined,
            title: page ? page.title : undefined,
            sentence: message,
            reply: "",
        };
    }

    // ---------- ۱۲) ناوبری ----------
    //
    // این قبل از «منو» می‌آید و دلیلش یک باگ واقعی است: در فارسی
    // محاوره‌ای «منو» هم یعنی «من را». جمله‌ی «منو ببر به گزارش
    // تخلفات» با تگ «منو» به فهرست دسترسی‌ها می‌خورد و کاربر به جای
    // رفتن، منو می‌گرفت. قاعده: اگر هم فعل رفتن هست و هم نام صفحه،
    // ناوبری برنده است.
    //
    // امتیاز تطبیق صفحه بر اساس *طول* تگ است، نه تعداد (در findPage).
    const target = findPage(n);
    if (isCommand(n) && target) return navigateTo(target, ctx.role);

    // ---------- ۱۳) منو و دسترسی‌ها ----------
    if (
        hasAny(n, [
            "چیکار میتونی", "چه کاری میتونی", "چی میتونی", "منو", "منوها", "فهرست",
            "چه دسترسی", "دسترسی ها", "چه صفحاتی", "چه بخش هایی", "امکانات",
            "چه گزارشی", "چه داده ای", "چه اطلاعاتی داری",
        ])
    ) {
        return { type: "menu", reply: "" };
    }

    // ---------- ۱۴) راهنما ----------
    if (hasAny(n, ["راهنما", "کمک", "help", "چطور کار میکنی", "چجوری استفاده کنم"])) {
        return { type: "help", reply: "" };
    }

    return null;
};

/** ساخت دستور ناوبری، با احترام به سطح دسترسی نقش. */
const navigateTo = (target: SitePage, role: number | null): Intent => {
    if (!canAccess(role, target.roles)) {
        return {
            type: "navigate",
            link: "",
            title: target.title,
            reply: "دسترسی شما به «" + target.title + "» باز نیست.",
        };
    }

    return {
        type: "navigate",
        link: target.link,
        title: target.title,
        reply: "می‌برمتان به «" + target.title + "».",
    };
};
